/**
 * What the on-screen controls hint prints — **the display half of the key map,
 * and only that.**
 *
 * Split from `keys.ts` when AD-021's row took that file past the size ceiling,
 * and the seam is real rather than only a size one: nothing here is read by the
 * mapping. `keys` and `action` are strings for a human; the literals the
 * mapping actually keys off are the consts in `keys.ts` and `bail-key.ts`, and
 * `keys.test.ts` is what holds the two lists to each other.
 */

export type ControlId =
  | "pitch"
  | "roll"
  | "yaw"
  | "throttle"
  | "fire"
  | "clearJam"
  | "bombRelease"
  | "bailOut"
  | "camera"
  | "lookAround"
  | "pause"
  | "muteAudio"
  | "timeCompress"
  | "map"
  | "preferences"
  /** Not a key at all — the hint's one line about the stick (`gamepad.ts`). */
  | "joystick";

export interface ControlBinding {
  readonly id: ControlId;
  /** What the hint panel prints. */
  readonly keys: string;
  readonly action: string;
}

/** Drives the on-screen controls hint; the mapping functions below key off the same literals. */
export const CONTROL_BINDINGS: readonly ControlBinding[] = [
  { id: "pitch", keys: "↑ / ↓  ·  W / S", action: "Stick forward / back (nose down / up)" },
  { id: "roll", keys: "← / → or A / D", action: "Ailerons — bank left / right" },
  { id: "yaw", keys: "Z / X  ·  , / .", action: "Rudder left / right" },
  { id: "throttle", keys: "+ / −  ·  Wheel", action: "Throttle up / down (mouse wheel over the view)" },
  { id: "fire", keys: "F  ·  Space  ·  Left-click", action: "Fire guns (click-and-hold over the view)" },
  { id: "clearJam", keys: "U", action: "Clear a gun jam (one attempt per press)" },
  { id: "bombRelease", keys: "B", action: "Let a bomb go — only when you are carrying any (hold to walk the stick off)" },
  {
    id: "bailOut",
    keys: "Hold H",
    action: "Bail out — hit the silk (hold for a second and a half; port addition, switch it off on PREFERENCES)",
  },
  { id: "camera", keys: "1 / 2 / 3  ·  C", action: "Chase / fly-by / cockpit camera — C cycles · Wheel zooms in fly-by" },
  {
    id: "lookAround",
    keys: "Q / E · R · T / G · Right-drag",
    action:
      "Glance left / right · look back · look up / down — or hold the right button and drag to look freely " +
      "(cockpit view — release to snap forward)",
  },
  { id: "pause", keys: "P", action: "Pause the simulation" },
  { id: "map", keys: "M", action: "Map — the region sheet, its sector grid and where you are on it (pauses the flight)" },
  {
    id: "preferences",
    keys: "O",
    action: "Preferences — every setting on one panel: display, audio, flying, realism (pauses the flight)",
  },
  { id: "muteAudio", keys: "V", action: "Mute / unmute audio" },
  { id: "timeCompress", keys: "K", action: "Time compress — skip a transit leg (blocked/dropped near a contact)" },
  {
    id: "joystick",
    keys: "Joystick",
    action: "Plug one in and press a button — a standard controller flies with no setup; CONTROLS on the PREFERENCES panel calibrates anything else",
  },
];
